import React, { useState } from 'react';
import AnnouncementEditor from './AnnouncementEditor';
import { useRefreshable } from '../context/RefreshContext';
import { currencySymbolOf, USER_TYPES } from '../constants/designer';
import { SHOP_SOCIALS, shopSocialHref, shopWebsiteHref } from '../constants/shop';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBasket } from '../context/BasketContext';
import { useT } from '../i18n';
import { detectPlatform, contactHref } from '../utils/contactPlatforms';
import { updateUserAvatar } from '../firebase/userService';
import useCredits from '../hooks/useCredits';
import StyleList from './StyleList';
import ActionIcon from './ActionIcon';
import { tierOf, TierBadge, TierCheck, TierCaps } from './TierMarks';
import AvatarLightbox from './AvatarLightbox';
import './DesignerProfileHeader.css';
import PersonIcon from './PersonIcon';

// "https://www.behance.net/anna?ref=x" → "behance.net/anna".
//
// A scheme and a "www." are pure boilerplate — every link has them and they
// identify nothing, so neither is ever shown. What remains is the host plus the
// first path segment, which is what actually names the link.
//
// Deliberately string work rather than `new URL()`. URL() succeeds on schemes
// that have no host at all and produces nonsense from them: `tel:+37477` parses
// with hostname "" and pathname "+37477", which rendered as "/+37477". It also
// left "www." in place on any value typed without a scheme, since the old
// fallback regex required "https://" before it.
function prettyHost(url) {
  const raw = String(url || '').trim();
  // Hostless schemes: strip the scheme and show the rest as written.
  if (/^(tel|mailto|sms):/i.test(raw)) return raw.replace(/^(tel|mailto|sms):/i, '');

  const bare = raw
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')   // any scheme, not just http(s)
    .replace(/^www\./i, '')
    .replace(/[?#].*$/, '')                    // query/hash add noise, never meaning
    .replace(/\/+$/, '');

  const [host, ...rest] = bare.split('/');
  const out = rest.length && rest[0] ? `${host}/${rest[0]}` : host;
  return out.slice(0, 44);
}

// ── PUBLISHED PROFILE HEADER ─────────────────────────────────────────────────
// The dashboard's identity strip: avatar, name, a compact facts line, and the
// workspace link pushed to the far right. Replaces the generic "My workspace"
// card, which spent a full-width panel restating what the button now says in
// one line — leaving the published work below to carry the page.
//
// BOTH PUBLISHING ROLES. A shop's header is the same strip with its own fields
// — categories instead of styles, delivery instead of experience, a storefront
// and socials instead of websites and contacts — and it is read off the same
// cached document. The role is taken from `profile`, not passed in, because
// this header only ever renders the SIGNED-IN user.
//
// The facts line is built from whatever the profile actually has. Empty fields
// are dropped rather than rendered as blanks, so a half-filled profile reads as
// deliberately brief instead of broken.
export default function DesignerProfileHeader() {
  const { user, profile, refreshProfile } = useAuth();
  const { count: basketCount } = useBasket();
  const { t } = useT();
  const navigate = useNavigate();
  const [viewingAvatar, setViewingAvatar] = useState(false);

  // The header renders entirely from the cached users/{uid} document, so a
  // refresh has to re-read it — otherwise the like count and rate here stay
  // frozen while the rest of the dashboard updates around them.
  useRefreshable(() => refreshProfile());

  // Saved immediately, not on a form submit — the lightbox has no Save button,
  // so choosing a picture IS the confirmation. `refreshProfile` is what makes
  // the header, the menu chip and Settings all show the new one at once.
  const saveAvatar = async (url) => {
    await updateUserAvatar(user.uid, url);
    await refreshProfile();
  };

  const isShop = profile?.userType === USER_TYPES.SHOP;
  const d = (isShop ? profile?.shop : profile?.designer) || {};
  const name = profile?.fullName || user?.displayName || user?.email?.split('@')[0] || '';

  // ── THE PAID-TIER TREATMENT ───────────────────────────────────────────────
  // The SAME marks the public page renders, from the same components — a
  // designer who is paying for gold has to see the thing they are paying for,
  // and seeing a plain header on their own dashboard reads as the plan not
  // having taken effect.
  //
  // FROM THE LIVE PLAN, not from `profile`. AuthContext fetches that document
  // once per session, so an upgrade would leave this header undecorated until a
  // full reload — the exact impression ("my plan did nothing") the marks exist
  // to prevent. useCredits already holds a snapshot listener on users/{uid} for
  // the balance, and the plan rides along on it; see the note in Plans.jsx.
  //
  // Shops are excluded here as on the public page.
  const { plan } = useCredits();
  // Both ladders decorate a header now; tierOf is told which to read.
  const tier = tierOf(plan, isShop);

  const facts = (isShop
    ? [
      d.location || null,
      d.delivery ? t('shop_delivery') : null,
    ]
    : [
      d.location || null,
      d.experienceYears != null && d.experienceYears !== ''
        ? t('ds_years_short').replace('{n}', d.experienceYears)
        : null,
      d.hourlyRate != null && d.hourlyRate !== ''
        ? `${d.hourlyRate} ${currencySymbolOf(d.currency)} ${t(`ds_per_${d.rateUnit || 'hour'}`)}`.trim()
        : null,
    ]).filter(Boolean);

  // A shop's single website and keyed socials, flattened to the same rows the
  // designer's two arrays produce — see the identical note in the public page.
  const shopLinks = isShop
    ? SHOP_SOCIALS
      .filter(s => String(d.socials?.[s.id] || '').trim())
      .map(s => ({
        key: s.id,
        href: shopSocialHref(s.id, d.socials[s.id]),
        label: s.free ? d.socials[s.id] : `${t(`shop_social_${s.id}`)} · ${String(d.socials[s.id]).replace(/^@+/, '')}`,
        newTab: !s.free,
      }))
    : [];

  // The designer's own like count. It lives on the same document search reads,
  // so this costs no extra fetch — and it's the one number about their profile
  // they otherwise can't see without visiting their own public page.
  const likeCount = profile?.likeCount || 0;

  return (
    // `dph--own` adds the actions column. Everything else is the SAME structure
    // the public profile uses (see pages/DesignerProfile.jsx), so a designer
    // sees their own page laid out exactly as visitors see it.
    <header className={`dph dph--own${tier ? ` dph--tier dph--${tier.id}` : ''}`}>
      {/* A button, not a div: opening the picture is an action, and it has to
          be reachable by keyboard like any other. */}
      <button
        type="button"
        className={`dph-avatar dph-avatar--btn${tier ? ' dph-avatar--tier' : ''}`}
        onClick={() => setViewingAvatar(true)}
        aria-label={t('avl_open')}
        title={t('avl_open')}
      >
        {profile?.avatarUrl
          ? <img src={profile.avatarUrl} alt="" />
          : <PersonIcon />}
        <span className="dph-avatar-veil" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
            strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9a2 2 0 0 1 2-2h1.6a2 2 0 0 0 1.7-.9l.8-1.2a2 2 0 0 1 1.7-.9h2.4a2 2 0 0 1 1.7.9l.8 1.2a2 2 0 0 0 1.7.9H19a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <circle cx="12" cy="13" r="3.2" />
          </svg>
        </span>
        {/* After the veil, so the camera hint fades up BEHIND the tick rather
            than over it. */}
        <TierCheck tier={tier} />
      </button>

      {viewingAvatar && (
        <AvatarLightbox
          src={profile?.avatarUrl || ''}
          name={name}
          editable
          onChange={saveAvatar}
          onClose={() => setViewingAvatar(false)}
        />
      )}

      {/* Identity — name with its like count under it. Split out of .dph-body
          so it can sit BESIDE the avatar while everything below (facts, bio,
          chips, contacts) runs full width. */}
      <div className="dph-ident">
        {/* The badge sits ON the name line rather than under it: it is an
            adjective about this person, and a row of its own reads as a
            separate fact. The row is here for every profile, tier or not, so
            the name does not shift when a plan is bought. */}
        <div className="dph-name-row">
          <h1 className="dph-name">{name}</h1>
          <TierBadge tier={tier} />
        </div>
        <p className="dph-likes" title={t('like_count')}>
          <span className="dph-likes-heart" aria-hidden="true">♥</span>
          <b>{likeCount}</b> {t('like_count')}
        </p>
      </div>

      <div className="dph-body">
        {facts.length > 0 && (
          <p className="dph-facts">
            {facts.map((f, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="dph-dot" aria-hidden="true">·</span>}
                {f}
              </React.Fragment>
            ))}
          </p>
        )}
        {/* What the tier actually buys, above the bio — it qualifies the facts
            line it follows, and stays clear of the style chips below, which say
            something else entirely. */}
        <TierCaps tier={tier} isShop={isShop} />

        {d.description && <p className="dph-bio">{d.description}</p>}

        {/* Chips rather than another comma list — reading them as tags is
            faster than parsing prose. Styles for a designer, categories for a
            shop; one component, two vocabularies. */}
        {isShop
          ? d.categories?.length > 0 && (
            <StyleList styles={d.categories} prefix="shop_cat_" moreLabel="shop_categories" />
          )
          : d.styles?.length > 0 && <StyleList styles={d.styles} />}

        {/* Websites and contacts are separate groups, each labelled. Merged into
            one row they read as an undifferentiated pile of links — and the two
            mean different things: where to see the work, versus how to make
            contact. */}
        <div className="dph-groups">
          {/* ── SHOP: storefront and socials ── */}
          {isShop && d.website && (
            <div className="dph-group">
              <span className="dph-group-label">{t('shop_website')}</span>
              <ul className="dph-links">
                <li>
                  <a href={shopWebsiteHref(d.website)} target="_blank"
                    rel="noopener noreferrer nofollow" className="dph-link">
                    {prettyHost(d.website)}
                  </a>
                </li>
              </ul>
            </div>
          )}
          {isShop && shopLinks.length > 0 && (
            <div className="dph-group">
              <span className="dph-group-label">{t('shop_socials')}</span>
              <ul className="dph-links">
                {shopLinks.map(l => (
                  <li key={l.key}>
                    <a href={l.href} target={l.newTab ? '_blank' : undefined}
                      rel="noopener noreferrer nofollow" className="dph-link">
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!isShop && d.websites?.length > 0 && (
            <div className="dph-group">
              <span className="dph-group-label">{t('ds_websites')}</span>
              <ul className="dph-links">
                {d.websites.map(w => (
                  <li key={w}>
                    {/* noopener/noreferrer on every user-supplied link — without
                        it the target page can reach back through window.opener. */}
                    <a href={w} target="_blank" rel="noopener noreferrer nofollow" className="dph-link">
                      {prettyHost(w)}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!isShop && d.contacts?.length > 0 && (
            <div className="dph-group">
              <span className="dph-group-label">{t('ds_contacts')}</span>
              <ul className="dph-links">
                {d.contacts.map((c, i) => {
                  // The SERVICE is what a visitor is looking for here, not the
                  // URL — "Telegram" answers "how do I reach them", "t.me/gev"
                  // makes them decode it. The handle rides along so two links
                  // to the same service stay distinguishable.
                  const p = detectPlatform(c.value, c.type);
                  const label = p.isKnown
                    ? (p.handle && p.key !== 'phone' && p.key !== 'email'
                        ? `${p.name} · ${p.handle}` : p.name || p.handle)
                    : prettyHost(c.value);
                  return (
                    <li key={`c${i}`}>
                      <a
                        href={contactHref(c.value, c.type)}
                        target={p.key === 'phone' || p.key === 'email' ? undefined : '_blank'}
                        rel="noopener noreferrer nofollow"
                        className="dph-link"
                        title={c.value}
                      >
                        {p.key === 'phone' ? c.value : label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* A designer can like other designers too, so they get the same pair of
          actions a customer does. */}
      <div className="dph-actions">
        {/* The workspace is where 3D projects live, and a shop has none — the
            button would lead to a permanently empty page. Its other two
            actions stay: a shop can follow designers and keep favourites like
            anyone else. */}
        {!isShop && (
          <button className="dph-workspace" onClick={() => navigate('/workspace')}>
            <ActionIcon name="workspace" />
            {t('dash_workspace_title')}
          </button>
        )}
        <button className="dph-workspace dph-workspace--alt" onClick={() => navigate('/my-designers')}>
          <ActionIcon name="designers" />
          {t('md_title')}
        </button>
        <button className="dph-workspace dph-workspace--alt" onClick={() => navigate('/my-liked')}>
          <ActionIcon name="liked" />
          {t('ml_title')}
        </button>
        {/* NOT FOR A SHOP. A basket of your own stock is not a thing — the
            products here are the ones you sell. */}
        {!isShop && (
          <button className="dph-workspace dph-workspace--alt" onClick={() => navigate('/basket')}>
            <ActionIcon name="basket" />
            {t('bsk_title')}
            {/* The count, only when there is one. "My basket 0" states an
                absence the empty page already explains better. */}
            {basketCount > 0 && <span className="dph-workspace-n">{basketCount}</span>}
          </button>
        )}

        {/* ── PLANS & CREDITS ───────────────────────────────────────────────
            LAST, and after the basket: the four above navigate to things the
            account already has — its projects, its designers, its favourites,
            its basket — and this one is about the account itself. Put first it
            would read as the primary thing to do on your own profile, which it
            is not.

            EVERY ROLE, unlike the workspace and basket buttons either side of
            it: a shop buys credits for AI staging exactly as a designer does,
            and the page branches to the right view on its own.

            The header's credits pill already links here, but it reads as a
            BALANCE — a number says how many you have, not that there is
            somewhere to go and get more. This is the named route.

            The tier badge beside the name says which plan is active, so this
            button does not repeat it.

            `--plans` gives it the travelling gradient edge. It is the only one
            of the five that moves, which is the entire point: motion here means
            "this is the one that is not like the others", and a column where
            every button animated would say nothing at all. */}
        <button
          className="dph-workspace dph-workspace--alt dph-workspace--plans"
          onClick={() => navigate('/plans')}
        >
          <ActionIcon name="plans" />
          {t('menu_plans')}
        </button>
      </div>

      {/* Spans the full grid, under the header content: the banner is about the
          whole profile rather than about any one column of it.
          NO ROLE GATE. Shops buy this on their own ladder now — silver one
          image, gold three, exactly as designers do — so the editor is rendered
          for both and told WHICH ladder to read. It returns null by itself on
          any tier without the feature, which is the check that matters. */}
      <div className="dph-announce">
        <AnnouncementEditor uid={user?.uid} plan={plan} isShop={isShop} />
      </div>
    </header>
  );
}
